import { Fragment, useEffect, useState } from "react";
import {
  Box,
  Button,
  Link,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  ActionType,
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  IntegrationType,
  useCreateClientIntegrationMutation,
  useTriggerActionLazyQuery,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { Command } from "~/components/guides/components";
import { getError } from "~/lib/handle-error";
import {
  DOMAIN_PATTERN,
  helperTextStyles,
  ValidationMessage,
} from "../../utils/integrationValidationUtils";
import { IntegrationRecommendedPolicies } from "../../components/IntegrationRecommendedPolicies";
import { IntegrationAddHeader } from "../../headers/IntegrationAddHeader";
import useGenerateIntegrationName from "../../utils/integrationsUtils";
import { UpdateFlowData } from "../../utils/integrationsTypes";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

type OktaFormInput = {
  integrationName: string;
  organization: string;
  token: string | null;
};

const defaultValues: OktaFormInput = {
  integrationName: "",
  organization: "",
  token: "",
};

export function OktaIntegrationForm({
  scope,
  updateFlow,
}: {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}) {
  let navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [view, setView] = useState<"setup" | "add policies">("setup");
  const defaultIntegrationName = useGenerateIntegrationName({ scope });

  const {
    control,
    handleSubmit,
    watch,
    reset,
    trigger,
    getFieldState,
    formState: { errors, isValid, isSubmitSuccessful },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const [organization, token] = watch(["organization", "token"]);

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const integrationMrn = data.createClientIntegration.integration.mrn;
      triggerClientIntegrationScan({
        variables: { input: { mrn: integrationMrn, type: ActionType.RunScan } },
      });
    },
    refetchQueries: [
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    onError(error) {
      console.log("%c Error Scheduling scan on creation", "color: tomato");
      console.log(error.message);
    },
  });

  useEffect(() => {
    if (updateFlow) {
      if (
        updateFlow.integration.configurationOptions?.__typename !==
        "OktaConfigurationOptions"
      )
        return;
      reset({
        integrationName: updateFlow.integration.name,
        organization: updateFlow.integration.configurationOptions?.organization,
      });
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    trigger("organization");
  }, [token]);

  useEffect(() => {
    trigger("token");
  }, [organization]);

  const onSubmit: SubmitHandler<OktaFormInput> = async (data) => {
    const oktaConfigurationOptions = {
      organization: data.organization,
      ...(updateFlow ? {} : { token: data.token }),
    };

    try {
      if (updateFlow) {
        await updateIntegration({
          variables: {
            input: {
              name: data.integrationName.trim(),
              mrn: getIntegrationMrn({
                scope,
                integrationId:
                  updateFlow?.integration.mrn.split("/").pop() || "",
              }),
              type: ClientIntegrationType.Okta,
              configurationOptions: {
                oktaConfigurationOptions,
              },
            },
          },
        });
        const integrationId = updateFlow.integration.mrn.split("/").pop();
        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });
        navigate(`/space/integrations/okta/${integrationId}/?${scope.params}`);
      } else {
        await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.Okta,
              longLivedToken: false,
              configurationOptions: {
                oktaConfigurationOptions,
              },
            },
          },
        });
        setView("add policies");
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  setDocumentTitle(["Okta", "Integrations Setup"]);

  return (
    <Fragment>
      {view === "setup" ? (
        <Fragment>
          <IntegrationAddHeader {...{ type: IntegrationType.Okta }} />
          <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Step 1 */}
              <Box pb={4}>
                <Command
                  number={1}
                  options={{
                    fontSize: { xs: 16 },
                    dotColor: theme.palette.background.lightest,
                  }}
                >
                  Choose an integration name
                </Command>

                <Box pl={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, mt: 2 }}
                  >
                    Please choose a descriptive name that lets you easily
                    identify your integration.
                  </Typography>
                  <Controller
                    name="integrationName"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        sx={{
                          background: theme.palette.code.background,
                          borderRadius: 1,
                          color: "text.primary",
                          ...helperTextStyles,
                        }}
                        placeholder="Your integration name..."
                        error={Boolean(errors.integrationName)}
                        helperText={
                          Boolean(errors.integrationName) && (
                            <ValidationMessage error={errors.integrationName} />
                          )
                        }
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Step 2 */}
              <Box pb={4}>
                <Command
                  number={2}
                  options={{
                    fontSize: { xs: 16 },
                    dotColor: theme.palette.background.lightest,
                  }}
                >
                  Enter the organization domain
                </Command>

                <Box pl={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, mt: 2 }}
                  >
                    If your domain includes '-admin', remove it when specifying
                    the organization to Mondoo. For example, if your Okta URL is
                    'dev-22556123-admin.okta.com', specify your organization as
                    'dev-22556123.okta.com'. To learn more, read the{" "}
                    <Link
                      href="https://developer.okta.com/docs/guides/find-your-domain/main/"
                      target="_blank"
                      rel="noopener"
                    >
                      Okta documentation
                    </Link>
                    .
                  </Typography>
                  <Controller
                    name="organization"
                    control={control}
                    rules={{
                      required: true,
                      validate: {
                        isDomain: (value) =>
                          Boolean(value.match(DOMAIN_PATTERN)) ||
                          "Not a valid domain",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        sx={{
                          background: theme.palette.code.background,
                          borderRadius: 1,
                          color: "text.primary",
                          ...helperTextStyles,
                        }}
                        placeholder={"your-account12345.okta.com"}
                        error={
                          getFieldState("organization").isTouched &&
                          Boolean(errors.organization)
                        }
                        helperText={
                          Boolean(errors.organization) &&
                          getFieldState("organization").isTouched && (
                            <ValidationMessage error={errors.organization} />
                          )
                        }
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Step 3 */}
              <Box pb={4}>
                <Command
                  number={3}
                  options={{
                    fontSize: { xs: 16 },
                    dotColor: theme.palette.background.lightest,
                  }}
                >
                  Enter the API token
                </Command>

                <Box pl={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, mt: 2 }}
                  >
                    Generate your API token from the Okta Admin Console. To
                    learn more, read the{" "}
                    <Link
                      href="https://developer.okta.com/docs/guides/create-an-api-token/main/"
                      target="_blank"
                      rel="noopener"
                    >
                      Okta documentation
                    </Link>
                    .
                  </Typography>
                  <Controller
                    name="token"
                    control={control}
                    rules={{
                      required: true,
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="password"
                        fullWidth
                        sx={{
                          background: theme.palette.code.background,
                          borderRadius: 1,
                          color: "text.primary",
                          ...helperTextStyles,
                        }}
                        placeholder={
                          "XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                        }
                        error={
                          getFieldState("token").isTouched &&
                          Boolean(errors.token)
                        }
                        helperText={
                          Boolean(errors.token) &&
                          getFieldState("token").isTouched && (
                            <ValidationMessage error={errors.token} />
                          )
                        }
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "end" }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  endIcon={<ChevronRightIcon />}
                  // setValue doesn't trigger the required validation so we're doing it manually here
                  disabled={!isValid}
                >
                  {updateFlow ? "update configuration" : "start scanning"}
                </Button>
              </Box>
            </form>
          </Box>
        </Fragment>
      ) : (
        <IntegrationRecommendedPolicies
          {...{ scope }}
          filterTypes={["okta-org"]}
        />
      )}
    </Fragment>
  );
}
